const ACCESS_KEY = 'bb_access_token';
const REFRESH_KEY = 'bb_refresh_token';
const ORG_SLUG = process.env.REACT_APP_ORG_SLUG || 'brightblue';

export type ApiErrorBody = {
  error: {
    code: string;
    message: string;
    details?: Array<{ field: string; code: string; message: string }>;
  };
};

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
};

function messagesFromApiBody(body: ApiErrorBody): string {
  const details = body.error?.details;
  const fromDetails = Array.isArray(details)
    ? details
        .map((row) => (row && typeof row.message === 'string' ? row.message.trim() : ''))
        .filter((message) => message.length > 0)
    : [];
  if (fromDetails.length > 0) return fromDetails.join(' ');
  const envelope = body.error?.message;
  return typeof envelope === 'string' ? envelope.trim() : '';
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody,
  ) {
    super(messagesFromApiBody(body) || `Request failed (${status})`);
  }
}

export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const fromBody = messagesFromApiBody(err.body);
    if (fromBody) return fromBody;
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function apiErrorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.body.error?.code ?? null : null;
}

export const PROJECT_MEMBERSHIP_MESSAGE = 'Not a project member.';

export function isProjectMembershipError(err: unknown): boolean {
  if (typeof err === 'string') return err === PROJECT_MEMBERSHIP_MESSAGE;
  return err instanceof Error && err.message === PROJECT_MEMBERSHIP_MESSAGE;
}

export function isAuthSession(value: unknown): value is AuthSession {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.accessToken === 'string' &&
    record.accessToken.length > 0 &&
    typeof record.refreshToken === 'string' &&
    record.refreshToken.length > 0
  );
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setAuthSession(session: AuthSession): void {
  if (!isAuthSession(session)) {
    throw new Error('Invalid auth session');
  }
  localStorage.setItem(ACCESS_KEY, session.accessToken);
  localStorage.setItem(REFRESH_KEY, session.refreshToken);
}

export function clearAuthSession(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function setAccessToken(token: string | null): void {
  if (token) localStorage.setItem(ACCESS_KEY, token);
  else clearAuthSession();
}

export function orgSlug(): string {
  return ORG_SLUG;
}

function userIdFromAccessToken(token: string): string | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload: unknown = JSON.parse(atob(padded));
    if (!payload || typeof payload !== 'object') return null;
    const sub = (payload as { sub?: unknown }).sub;
    return typeof sub === 'string' && sub.length > 0 ? sub : null;
  } catch {
    return null;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

async function rotateSession(): Promise<boolean> {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  if (!accessToken || !refreshToken) return false;
  const userId = userIdFromAccessToken(accessToken);
  if (!userId) return false;

  const res = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, refreshToken }),
  });
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }
  if (!res.ok || !isAuthSession(json)) {
    clearAuthSession();
    return false;
  }
  setAuthSession(json);
  return true;
}

function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = rotateSession().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  return requestOnce<T>(path, init, false);
}

/** Multipart upload. Do not set Content-Type so the browser can add the boundary. */
export async function apiUpload<T>(path: string, file: File, fields: Record<string, string> = {}): Promise<T> {
  if (!(file instanceof File)) {
    throw new Error('apiUpload requires a File.');
  }
  const form = new FormData();
  form.append('file', file);
  for (const [key, value] of Object.entries(fields)) {
    form.append(key, value);
  }
  return requestOnce<T>(path, { method: 'POST', body: form, headers: new Headers() }, false);
}

async function requestOnce<T>(
  path: string,
  init: RequestInit,
  retried: boolean,
): Promise<T> {
  const token = getAccessToken();
  const headers = new Headers(init.headers);
  const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (!headers.has('Content-Type') && init.body && !isForm) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const res = await fetch(path, { ...init, headers });
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }
  if (!res.ok) {
    if (res.status === 401 && !retried && path !== '/api/auth/refresh') {
      const refreshed = await refreshSession();
      if (refreshed) {
        return requestOnce<T>(path, init, true);
      }
    }
    const body = (json && typeof json === 'object' && 'error' in (json as object)
      ? json
      : { error: { code: 'UNAUTHENTICATED', message: text || res.statusText } }) as ApiErrorBody;
    throw new ApiError(res.status, body);
  }
  return json as T;
}

export function v1Path(suffix: string): string {
  const clean = suffix.startsWith('/') ? suffix : `/${suffix}`;
  return `/api/v1/orgs/${orgSlug()}${clean}`;
}

/** Authenticated binary fetch. `<img src>` cannot send the Bearer token. */
export async function apiFetchBlob(path: string): Promise<Blob> {
  if (typeof path !== 'string' || path.length === 0) {
    throw new Error('apiFetchBlob requires a path.');
  }
  return requestBlobOnce(path, false);
}

async function requestBlobOnce(path: string, retried: boolean, cache: RequestCache = 'no-store'): Promise<Blob> {
  const token = getAccessToken();
  const headers = new Headers();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const res = await fetch(path, { headers, cache });
  // Express ETags turn a repeat download into 304 with no body. fetch() then
  // exposes 304 (not ok) or an empty blob — never an image we can render.
  if (res.status === 304 && cache !== 'reload') {
    return requestBlobOnce(path, retried, 'reload');
  }
  if (!res.ok) {
    if (res.status === 401 && !retried && path !== '/api/auth/refresh') {
      const refreshed = await refreshSession();
      if (refreshed) {
        return requestBlobOnce(path, true, cache);
      }
    }
    const text = await res.text();
    let json: unknown = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
    }
    const body = (json && typeof json === 'object' && 'error' in (json as object)
      ? json
      : { error: { code: 'UNAUTHENTICATED', message: text || res.statusText } }) as ApiErrorBody;
    throw new ApiError(res.status, body);
  }
  return res.blob();
}
